import { DragEvent, FC, ReactElement, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Modal } from 'reactstrap'

import { Icon, LoadingSpinner } from '../../../../../@paco/components'
import CalendarHeader from '../../../../../@paco/components/CalendarHeader/CalendarHeader'
import { IconButton, IconNavLink } from '../../../../../@paco/compositions'
import { getDayNumberFromDate } from '../../../../../@paco/helpers/date'
import useDeviceWidth from '../../../../../@paco/helpers/hooks/useDeviceWidth'
import { useScrollSnapCarousel } from '../../../../../@paco/helpers/hooks/useScrollSnapCarousel'
import trans from '../../../../../@paco/helpers/trans'
import { ScheduleRoutes } from '../../../../../@paco/routes/helpers'
import { PrivateRouteGroupRoutes } from '../../../../../@paco/routes/routes'
import { TimeModeType } from '../../../../../@paco/types'

import { BaseScheduleViewModel } from '../../../../../entities/BaseSchedule/BaseSchedule'
import { getBaseScheduleCommentsForWeek } from '../../../../../entities/BaseSchedule/helpers/baseScheduleCommentHelpers'
import { BaseScheduleShiftViewModel } from '../../../../../entities/BaseScheduleShift/BaseScheduleShift'
import addDatesToBaseScheduleShift from '../../../../../entities/BaseScheduleShift/helpers/addDatesToBaseScheduleShift'
import { isSameDay } from '../../../../../helpers/date'
import { ShiftViewModel } from '../../../../../models'
import CopyShiftsForm from '../../../forms/CopyShiftsForm/CopyShiftsForm'
import ConceptsCalendar from '../../ConceptsCalendar/ConceptsCalendar'

import './BaseScheduleCalendar.scss'

interface BaseScheduleCalendarProps {
    isLoading: boolean;
    baseSchedule?: BaseScheduleViewModel;
    highlightShiftId?: string;
    selectedDaysInWeeks: Date[][];
    shiftsByWeek: BaseScheduleShiftViewModel[][];
    selectedWeekIndex: number;
    canAddBaseScheduleShifts: boolean;
    canEditBaseScheduleShifts: boolean;
    onWeekIndexChange: (weekIndex: number) => void;
    onAddShift: (shiftDate: Date) => void;
    onCopyShiftsSubmit: (shift: BaseScheduleShiftViewModel) => void;
    onDeleteComment: (commentId: string) => void;
    onAddComment: (commentDate: Date) => void;
    onAddWeek: () => void;
    onRemoveWeek: () => void;
    className?: string;
}

interface CopyTarget {
    weekNumber: number;
    dayNumber: number;
}

const BaseScheduleCalendar: FC<BaseScheduleCalendarProps> = ({
    isLoading,
    baseSchedule,
    selectedDaysInWeeks,
    shiftsByWeek,
    selectedWeekIndex,
    canAddBaseScheduleShifts,
    canEditBaseScheduleShifts,
    onWeekIndexChange,
    onAddShift,
    onCopyShiftsSubmit,
    highlightShiftId,
    onDeleteComment,
    onAddComment,
    onAddWeek,
    onRemoveWeek,
    className = '',
}): ReactElement => {
    const navigate = useNavigate()
    const { isMobile, isTabletPortrait } = useDeviceWidth()

    const [selectedShiftIds, setSelectedShiftIds] = useState<string[]>([])
    const [draggingShiftId, setDraggingShiftId] = useState<string | null>(null)
    const [selectedCopyDate, setSelectedCopyDate] = useState<Date | null>(null)
    const [copyTarget, setCopyTarget] = useState<CopyTarget | null>(null)
    const [copyShiftsModalIsActive, setCopyShiftsModalIsActive] = useState(false)

    const allShifts = useMemo(() => shiftsByWeek.flat(), [shiftsByWeek])

    const initialWeekIndex = useMemo(
        () => Math.min(Math.max(selectedWeekIndex, 0), selectedDaysInWeeks.length - 1),
        [selectedWeekIndex, selectedDaysInWeeks.length],
    )

    const handleCloseCopyShiftsModal = () => setCopyShiftsModalIsActive(false)

    const handleDeleteShifts = (shiftIds: string[]) => {
        setSelectedShiftIds(shiftIds)
    }

    const handleCopyShiftsToDate = (shiftIds: string[], targetDate: Date) => {
        const targetWeekIndex = selectedDaysInWeeks.findIndex(weekDays => (
            weekDays.some(day => isSameDay(day, targetDate))
        ))

        if (targetWeekIndex < 0) {
            return
        }

        const targetWeekNumber = targetWeekIndex + 1
        const targetDayNumber = getDayNumberFromDate(targetDate)
        const sourceShift = allShifts.find(shift => shift.id === shiftIds[0])

        if (
            sourceShift
            && sourceShift.weekNumber === targetWeekNumber
            && sourceShift.dayNumber === targetDayNumber
        ) {
            return
        }

        setSelectedShiftIds(shiftIds)
        setCopyTarget({
            weekNumber: targetWeekNumber,
            dayNumber: targetDayNumber,
        })
        setCopyShiftsModalIsActive(true)
    }

    const handleSelectedShiftsChange = (shiftIds: string[]) => {
        setSelectedShiftIds(shiftIds)
    }

    const handleCopyShiftsSubmit = (copyEmployees: boolean) => {
        const shiftToCopy = allShifts.find(shift => shift.id === selectedShiftIds[0])

        if (! shiftToCopy || ! copyTarget) {
            return
        }

        shiftToCopy.weekNumber = copyTarget.weekNumber
        shiftToCopy.dayNumber = copyTarget.dayNumber

        if (! copyEmployees) {
            shiftToCopy.shiftPlannings = []
        }

        setCopyShiftsModalIsActive(false)
        setDraggingShiftId(null)
        setSelectedCopyDate(null)
        setCopyTarget(null)
        onCopyShiftsSubmit(shiftToCopy)
    }

    const handleShiftClick = (shift: ShiftViewModel) => navigate(`${PrivateRouteGroupRoutes.shifts()}/${ScheduleRoutes.baseScheduleShift(shift.id)}`)

    const getDragShiftsForWeek = (weekIndex: number, weekDays: Date[]): BaseScheduleShiftViewModel[] => {
        const weekShifts = shiftsByWeek[weekIndex] || []

        if (! draggingShiftId) {
            return weekShifts
        }

        if (weekShifts.some(shift => shift.id === draggingShiftId)) {
            return weekShifts
        }

        const sourceShift = allShifts.find(shift => shift.id === draggingShiftId)

        if (! sourceShift) {
            return weekShifts
        }

        return [...weekShifts, addDatesToBaseScheduleShift(sourceShift, weekDays)]
    }

    const {
        next,
        prev,
        dragPrev,
        dragNext,
        cancelDrag,
        items,
        itemRefs,
        containerRef,
        currentIndex,
        scrollTo,
    } = useScrollSnapCarousel(
        selectedDaysInWeeks.map((selectedDays, weekIndex) => (
            <>
                <CalendarHeader
                    days={selectedDays}
                    mode={TimeModeType.week}
                    className="base-schedule-calendar__calendar-header"
                />

                <ConceptsCalendar
                    isMobile={isMobile || isTabletPortrait}
                    showAddButton={canAddBaseScheduleShifts}
                    canEditBaseScheduleShifts={canEditBaseScheduleShifts}
                    dayHeaderFormat="EEEE"
                    deleteButtonId="delete-button"
                    highlightShiftId={highlightShiftId}
                    shifts={shiftsByWeek[weekIndex] || []}
                    dragShifts={getDragShiftsForWeek(weekIndex, selectedDays)}
                    selectedDays={selectedDays}
                    comments={getBaseScheduleCommentsForWeek(baseSchedule?.comments || [], weekIndex)}
                    draggingShiftId={draggingShiftId}
                    selectedCopyDate={selectedCopyDate}
                    selectedShiftIds={selectedShiftIds}
                    onDraggingShiftIdChange={setDraggingShiftId}
                    onSelectedCopyDateChange={setSelectedCopyDate}
                    onAddButtonClick={onAddShift}
                    onShiftClick={handleShiftClick}
                    onSelectedShiftsChange={handleSelectedShiftsChange}
                    onDeleteShifts={handleDeleteShifts}
                    onCopyShiftsToDate={handleCopyShiftsToDate}
                    onAddComment={onAddComment}
                    onDeleteComment={onDeleteComment}
                    className="base-schedule-calendar__calendar"
                />
            </>
        )),
        { initialIndex: initialWeekIndex },
    )

    const totalWeeks = baseSchedule?.totalWeeks ?? selectedDaysInWeeks.length
    const isLastWeek = currentIndex === items.length - 1

    const handleDragPrev = (event: DragEvent<HTMLButtonElement>) => {
        event.preventDefault()
        dragPrev()
    }

    const handleDragNext = (event: DragEvent<HTMLButtonElement>) => {
        event.preventDefault()
        dragNext()
    }

    useEffect(() => {
        if (currentIndex >= selectedDaysInWeeks.length && selectedDaysInWeeks.length > 0) {
            scrollTo(selectedDaysInWeeks.length - 1)
        }
    }, [currentIndex, scrollTo, selectedDaysInWeeks.length])

    useEffect(() => {
        onWeekIndexChange(currentIndex)
    }, [currentIndex])

    return (
        <div className={`base-schedule-calendar ${className}`}>
            <div className="base-schedule-calendar__top-bar">
                <h1 className="base-schedule-calendar__title">
                    {baseSchedule?.name}
                </h1>
                <span>
                    Week
                    {' '}
                    {currentIndex + 1}
                    {' / '}
                    {baseSchedule?.totalWeeks ?? selectedDaysInWeeks.length}
                </span>
                <div className="base-schedule-calendar__top-bar-actions">
                    {isLastWeek && canEditBaseScheduleShifts && totalWeeks > 1 && (
                        <IconButton
                            type="button"
                            icon="trash"
                            text={trans('pages.baseScheduleDetail.removeWeek')}
                            onClick={onRemoveWeek}
                            className="base-schedule-calendar__remove-week-button"
                            iconClassName="base-schedule-calendar__remove-week-button-icon"
                        />
                    )}
                    <IconNavLink
                        icon="reset"
                        text={trans('common.back')}
                        to={`${PrivateRouteGroupRoutes.shifts()}/${ScheduleRoutes.baseSchedules()}`}
                        className="base-schedule-calendar__back-button"
                        iconClassName="base-schedule-calendar__back-button-icon"
                    />
                </div>
            </div>

            {currentIndex !== 0 && (
                <button
                    type="button"
                    onClick={prev}
                    onDragOver={handleDragPrev}
                    onDragLeave={cancelDrag}
                    className="base-schedule-calendar__carousel-button base-schedule-calendar__carousel-button--left"
                >
                    <Icon name="chevron-left" />
                </button>
            )}

            <div
                className="base-schedule-calendar__scroll-container"
                ref={containerRef}
            >
                <div className="base-schedule-calendar__calendar-container">
                    {items.map((item, index) => (
                        <div
                            ref={itemRefs[index]}
                            key={index}
                            className="base-schedule-calendar__week-container"
                        >
                            {item}
                        </div>
                    ))}
                </div>
            </div>

            {currentIndex !== items.length - 1 && (
                <button
                    type="button"
                    onClick={next}
                    onDragOver={handleDragNext}
                    onDragLeave={cancelDrag}
                    className="base-schedule-calendar__carousel-button base-schedule-calendar__carousel-button--right"
                >
                    <Icon name="chevron-right" />
                </button>
            )}

            {isLastWeek && canEditBaseScheduleShifts && (
                <button
                    type="button"
                    onClick={onAddWeek}
                    className="base-schedule-calendar__carousel-button base-schedule-calendar__carousel-button--add"
                >
                    <Icon name="plus" />
                </button>
            )}

            {isLoading && (
                <LoadingSpinner />
            )}
            <Modal
                size="md"
                isOpen={copyShiftsModalIsActive}
                returnFocusAfterClose={false}
                className="concepts-calendar-overview__copy-shifts-form"
            >
                <CopyShiftsForm
                    shiftsLength={selectedShiftIds.length}
                    onSubmit={handleCopyShiftsSubmit}
                    onCancel={handleCloseCopyShiftsModal}
                />
            </Modal>
        </div>
    )
}

export default BaseScheduleCalendar
